import { useEffect, useState, useRef, useMemo } from "react"

import { FixedSizeList as List } from "react-window"

import { Check, ChevronsDownUp, ChevronsUpDown } from "lucide-react"

import { Button } from "@ui/button"
import { Input } from "@ui/input"
import { Label } from "@ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@ui/popover"
import { formatPropertyName } from "@/lib/properties"
import { PropertyFacet } from "@/models/facets"

const MAX_ITEMS_WITHOUT_FILTER = 100
const ITEM_HEIGHT = 40
const MAX_LIST_HEIGHT = 200

interface ProjectSearchPropertySectionProps {
    properties: PropertyFacet[]
    onSelection?: (selections: Record<string, string[] | undefined>) => void
    onMatchChange?: (selections: Record<string, string[] | undefined>) => void
    onClear: number
}
export function ProjectSearchPropertySection({ properties, onSelection, onMatchChange, onClear }: Readonly<ProjectSearchPropertySectionProps>) {
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

    const [propertyValues, setPropertyValues] = useState<Record<string, string[]>>({})
    const [filterValues, setFilterValues] = useState<Record<string, string>>({})
    const [openPopovers, setOpenPopovers] = useState<Record<string, boolean>>({})

    const handleSelectChange = (propertyId: string, value: string) => {
        const match = properties.some(prop => prop.field === propertyId && prop.match)
        const currentValues = propertyValues[propertyId] || []

        // Toggle the value: remove if already selected, add if not
        const newValuesForProperty = currentValues.includes(value)
            ? currentValues.filter(v => v !== value)
            : [...currentValues, value]

        const newValues = { ...propertyValues, [propertyId]: newValuesForProperty }
        setPropertyValues(newValues)

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        const cleanedValues = Object.fromEntries(
            Object.entries(newValues).filter(([key, value]) =>
                value !== null && value !== undefined && Array.isArray(value) && value.length > 0
            )
        )

        if (match) {
            onMatchChange?.(cleanedValues)
        } else {
            onSelection?.(cleanedValues)
        }
    }

    const handleClearSelect = (propertyId: string) => {
        const match = properties.some(prop => prop.field === propertyId && prop.match)
        const newValues = { ...propertyValues, [propertyId]: [] }
        setPropertyValues(newValues)

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        setOpenPopovers(prev => ({ ...prev, [propertyId]: false }))

        // Create cleaned values with other properties, and explicitly set the cleared property to undefined
        const cleanedValues: Record<string, string[] | undefined> = Object.fromEntries(
            Object.entries(newValues)
                .filter(([key, value]) =>
                    key !== propertyId && value !== null && value !== undefined && Array.isArray(value) && value.length > 0
                )
        )

        // Explicitly set the cleared property to undefined
        cleanedValues[propertyId] = undefined

        if (match) {
            onMatchChange?.(cleanedValues)
        } else {
            onSelection?.(cleanedValues)
        }
    }

    const handleFilterChange = (propertyId: string, filterValue: string) => {
        setFilterValues(prev => ({
            ...prev,
            [propertyId]: filterValue
        }))
    }

    const handlePopoverOpenChange = (propertyId: string, open: boolean) => {
        setOpenPopovers(prev => ({ ...prev, [propertyId]: open }))

        if (!open) {
            setFilterValues(prev => ({ ...prev, [propertyId]: "" }))
        }
    }

    const getFilteredValues = useMemo(() => {
        return (property: PropertyFacet) => {
            const filter = filterValues[property.field]?.toLowerCase() || ""
            const currentValues = propertyValues[property.field] || []

            // Filter out null/empty values
            let allValues = property.values.filter(value =>
                value && value._id !== null && value._id !== ""
            )

            // Ensure selected values are included even if they're not in property.values
            // This handles cases where selecting a value filters results and the value disappears from facets
            const selectedValuesFromFacets = allValues.filter(value =>
                currentValues.includes(value._id)
            )

            // Add any selected values that aren't in the current facet values
            const missingSelectedValues = currentValues
                .filter(val => !allValues.some(v => v._id === val))
                .map(val => ({ _id: val, count: 0 }))

            const selectedValues = [...selectedValuesFromFacets, ...missingSelectedValues]

            const unselectedValues = allValues.filter(value =>
                !currentValues.includes(value._id)
            )

            // Apply filter to unselected values only
            let filteredUnselectedValues = unselectedValues
            if (filter) {
                filteredUnselectedValues = unselectedValues.filter(value =>
                    String(value._id).toLowerCase().includes(filter)
                )
            } else {
                filteredUnselectedValues = unselectedValues
                    .sort((a, b) => (b.count || 0) - (a.count || 0))
                    .slice(0, MAX_ITEMS_WITHOUT_FILTER)
            }

            // Return selected values first, then filtered unselected values
            return {
                selectedValues,
                filteredValues: filteredUnselectedValues,
                allFilteredValues: [...selectedValues, ...filteredUnselectedValues]
            }
        }
    }, [filterValues, propertyValues])

    const getEmptyValueCount = (property: PropertyFacet) => {
        const emptyValue = property.values.find(value => !value._id || value._id === "")
        return emptyValue ? emptyValue.count : null
    }

    const handleOnSelectionChange = () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        const cleanedValues = Object.fromEntries(
            Object.entries(propertyValues).filter(([key, value]) =>
                value !== null && value !== undefined && Array.isArray(value) && value.length > 0
            )
        );
        onSelection?.(cleanedValues)
    }

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation()
    }

    const handleInputChange = (propertyId: string, e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation()
        handleFilterChange(propertyId, e.target.value)
    }

    useEffect(() => {
        setPropertyValues({})
        setFilterValues({})
        setOpenPopovers({})

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }
    }, [onClear])

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [])

    return (
        <div className={"flex w-full min-w-0 flex-col border-b gap-2 p-4"}>
            <Label className={"shrink-0 items-center rounded-md text-base font-medium text-sidebar-foreground"}>Search By</Label>
            <div className="flex flex-col gap-2">
                {
                    properties?.map((property) => {
                        const { selectedValues, filteredValues, allFilteredValues } = getFilteredValues(property)
                        const hasFilter = filterValues[property.field]?.length > 0
                        const emptyValueCount = getEmptyValueCount(property)
                        const isOpen = openPopovers[property.field] || false
                        const currentValues = propertyValues[property.field] || []
                        const shouldUseVirtualList = allFilteredValues.length > 50
                        const hasSelectedValues = selectedValues.length > 0

                        // Generate display text for multiple selections
                        const displayText = currentValues.length === 0
                            ? `Select ${formatPropertyName(property.name)}`
                            : currentValues.length === 1
                                ? currentValues[0]
                                : `${currentValues.length} selected`

                        return (
                            <div key={property.field}>
                                <Label className="text-xs">{formatPropertyName(property.name)}</Label>
                                <Popover
                                    open={isOpen}
                                    onOpenChange={(open) => handlePopoverOpenChange(property.field, open)}
                                >
                                    <PopoverTrigger asChild>
                                        <div className='flex flex-row border gap-2 items-center justify-center cursor-pointer hover:bg-gray-200 rounded-md h-10 px-3 bg-white'>
                                            <div className='w-full text-sm font-light text-left text-primary truncate'>
                                                {displayText}
                                            </div>
                                            <div className='border-l pl-2'>
                                                {
                                                    isOpen
                                                        ? <ChevronsDownUp className="h-4 w-4 opacity-50" />
                                                        : <ChevronsUpDown className="h-4 w-4 opacity-50" />
                                                }
                                            </div>
                                        </div>
                                    </PopoverTrigger>
                                    <PopoverContent className="max-w-[257px] p-2 border shadow z-60" align="start">
                                        <Input
                                            placeholder={`Filter ${formatPropertyName(property.name)}...`}
                                            value={filterValues[property.field] || ""}
                                            onChange={(e) => handleInputChange(property.field, e)}
                                            onKeyDown={handleInputKeyDown}
                                            onClick={(e) => e.stopPropagation()}
                                            className='mb-2'
                                        />
                                        {
                                            !hasFilter && (
                                                <div className="mb-2 text-xs text-muted-foreground px-2">
                                                    Showing top {Math.min(filteredValues.length, MAX_ITEMS_WITHOUT_FILTER)} results. Use filter to search all.
                                                </div>
                                            )
                                        }
                                        <div className={`flex flex-col gap-1 ${shouldUseVirtualList ? "" : "max-h-[200px] overflow-y-auto"}`}>
                                            {
                                                (!filterValues[property.field] || filterValues[property.field] === "") && (
                                                    <Button
                                                        variant="ghost"
                                                        className='flex flex-row items-center justify-between p-2 h-auto'
                                                        onClick={() => handleClearSelect(property.field)}
                                                        type="button"
                                                    >
                                                        <div className="flex items-center justify-between w-full text-left">
                                                            <span className="truncate flex-1 mr-2 text-sm">
                                                                Clear all
                                                            </span>
                                                            {
                                                                (currentValues.length === 0) && (
                                                                    <div className="flex items-center gap-2">
                                                                        <Check className="h-4 w-4" />
                                                                    </div>
                                                                )
                                                            }
                                                        </div>
                                                    </Button>
                                                )
                                            }
                                            {
                                                allFilteredValues.length > 0 ? (
                                                    shouldUseVirtualList ? (
                                                        <List
                                                            height={Math.min(allFilteredValues.length * ITEM_HEIGHT, MAX_LIST_HEIGHT)}
                                                            width="100%"
                                                            itemCount={allFilteredValues.length}
                                                            itemSize={ITEM_HEIGHT}
                                                            itemData={{
                                                                items: allFilteredValues,
                                                                currentValues,
                                                                handleSelectChange: (value: string) => handleSelectChange(property.field, value)
                                                            }}
                                                        >
                                                            {VirtualListItem}
                                                        </List>
                                                    ) : (
                                                        <>
                                                            {/* Render selected values first */}
                                                            {selectedValues.map((value) => (
                                                                <Button
                                                                    key={value._id}
                                                                    variant="ghost"
                                                                    className='flex flex-row items-center justify-between p-2 h-auto bg-accent/50'
                                                                    onClick={() => handleSelectChange(property.field, value._id)}
                                                                >
                                                                    <div className="flex items-center justify-between w-full text-left">
                                                                        <span className="truncate flex-1 text-sm" title={value._id}>
                                                                            {value._id}
                                                                        </span>
                                                                        <div className="flex items-center gap-2">
                                                                            <Check className="h-4 w-4" />
                                                                        </div>
                                                                    </div>
                                                                </Button>
                                                            ))}
                                                            {/* Add separator if there are both selected and filtered values */}
                                                            {hasSelectedValues && filteredValues.length > 0 && (
                                                                <div className="border-t my-1" />
                                                            )}
                                                            {/* Render filtered unselected values */}
                                                            {filteredValues.map((value) => (
                                                                <Button
                                                                    key={value._id}
                                                                    variant="ghost"
                                                                    className='flex flex-row items-center justify-between p-2 h-auto'
                                                                    onClick={() => handleSelectChange(property.field, value._id)}
                                                                >
                                                                    <div className="flex items-center justify-between w-full text-left">
                                                                        <span className="truncate flex-1 text-sm" title={value._id}>
                                                                            {value._id}
                                                                        </span>
                                                                    </div>
                                                                </Button>
                                                            ))}
                                                        </>
                                                    )
                                                ) : (
                                                    <div className="p-2 text-sm text-muted-foreground text-center">
                                                        No options found
                                                    </div>
                                                )
                                            }
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        )
                    })
                }
            </div>
        </div>
    )
}

const VirtualListItem = ({ index, style, data }: { index: number, style: any, data: any }) => {
    const { items, currentValues, handleSelectChange } = data
    const value = items[index]

    return (
        <div style={style}>
            <Button
                variant="ghost"
                className='flex flex-row items-center justify-between p-2 h-auto w-full'
                onClick={() => handleSelectChange(value._id)}
            >
                <div className="flex items-center justify-between w-full text-left">
                    <span className="truncate flex-1 text-sm" title={value._id}>
                        {value._id}
                    </span>
                    {
                        (currentValues.includes(value._id)) && (
                            <div className="flex items-center gap-2">
                                <Check className="h-4 w-4" />
                            </div>
                        )
                    }
                </div>
            </Button>
        </div>
    )
}